// based on https://dexie.org/docs/Typescript
import type {
  CachedBindings,
  PartialEvaluation,
  QueryResolver,
} from '@jetfabric/conditional-expressions/lib/data-reference-resolver';
import { V2Event, V2_EVENT_TYPES } from '@jetfabric/schemas/lib/protocol-sdk-api-v2/events';
import { ClientEventQueryV1 } from '@jetfabric/schemas/lib/protocol-sdk-api-v2/query/client-event-query-schema';
import { ConditionalExpression } from '@jetfabric/schemas/lib/protocol-sdk-api-v2/query/condition';
import {
  QueryClientEvent,
  V2Query,
} from '@jetfabric/schemas/lib/protocol-sdk-api-v2/query/queries';
import Dexie from 'dexie';
import { JetfabricError } from '../../errors';
import { random as _random } from '../../util/random';
import { timestampFactory as _timestampFactory } from '../../util/time';
import type { evaluate as _evaluate } from '@jetfabric/conditional-expressions/lib/main';
import {
  Either,
  isLeft,
  isRight,
  left,
  right,
} from '@jetfabric/conditional-expressions/lib/either';
import { TraceState } from '@jetfabric/schemas/lib/protocol-sdk-api-v2/query/trace';

export type ClientEventQueryResult = Array<V2Event> | number;

export const dbName = (storageKeyPrefix: string) => `${storageKeyPrefix}_client_events`;
// Dexie table schema description: https://dexie.org/docs/Version/Version.stores()#detailed-schema-syntax
// See example under "Logical AND" section of https://www.codeproject.com/Articles/744986/How-to-do-some-magic-with-indexedDB
// Compound primary key for table:
const PRIMARY_KEY = '[event.type+timestamp+random]';
const TABLE_NAME = 'events';

const CURRENT_VERSION = 1;
const RANDOM_MAX = 1000;

const MAX_ERRORS_REPORTED = 5;

// The filter assertions shouldn't contain any data references, so it doesn't matter how we resolve them
const throwingQueryResolver: QueryResolver = (q: V2Query) => {
  throw new Error(
    `Client event filter should not evaluate to partial result, query was ${JSON.stringify(q)}`
  );
};

interface InteralClientEvent {
  timestamp: number;
  random: number;
  event: V2Event;
}

export class ClientEventTables extends Dexie {
  private events: Dexie.Table<InteralClientEvent, number>;
  private timestampFactory: typeof _timestampFactory;
  private random: typeof _random;
  private maxEventCount: number;
  private evaluateFn: typeof _evaluate;

  private getRange(
    eventType: V2_EVENT_TYPES,
    startTimestamp?: number,
    reverse?: boolean,
    limit?: number
  ): Dexie.Collection<InteralClientEvent, number> {
    let range = this.events
      .where(PRIMARY_KEY)
      .between([eventType, startTimestamp || 0, 0], [`${eventType}\uffff`, 0, 0]);
    if (reverse === true) {
      range = range.reverse();
    }
    if (limit !== undefined) {
      range = range.limit(limit);
    }
    return range;
  }

  private bindingsWithEvent(timestamp: number, event: V2Event, bindingsStack: CachedBindings[]) {
    return bindingsStack.concat({
      bindings: { timestamp, ...event },
      cache: {},
    });
  }

  constructor(
    storageKeyPrefix: string,
    timestampFactory: typeof _timestampFactory,
    random: typeof _random,
    maxEventCount: number,
    evaluate: typeof _evaluate
  ) {
    super(dbName(storageKeyPrefix));
    this.timestampFactory = timestampFactory;
    this.random = random;
    this.maxEventCount = maxEventCount;
    this.evaluateFn = evaluate;
    this.version(CURRENT_VERSION).stores({
      events: PRIMARY_KEY,
    });
    this.events = this.table(TABLE_NAME);
  }

  async add(event: V2Event): Promise<number | undefined> {
    await this.maybeGC();
    if (event.type === V2_EVENT_TYPES.PAGE_VIEW) {
      return this.events.put({
        timestamp: +this.timestampFactory(),
        random: Math.floor(this.random() * RANDOM_MAX),
        event,
      });
    }
  }

  async maybeGC(): Promise<void> {
    const eventCount = await this.events.count();
    if (eventCount > this.maxEventCount) {
      // if the number of events in our table are over the GC limit,
      // delete the excess.
      await this.events
        .orderBy(PRIMARY_KEY)
        .limit(eventCount - this.maxEventCount + 1)
        .delete();
    }
  }

  async evaluateFilter(
    expression: ConditionalExpression,
    timestamp: number,
    event: V2Event,
    bindingsStack: CachedBindings[],
    traceState?: TraceState
  ): Promise<{ value: boolean } | { error: string }> {
    try {
      const result = await this.evaluateFn(
        expression,
        throwingQueryResolver,
        this.bindingsWithEvent(timestamp, event, bindingsStack),
        traceState
      );
      return { value: !!result.value };
    } catch (e) {
      return { error: e.message };
    }
  }

  async query(
    query: QueryClientEvent,
    queryResolver: QueryResolver,
    bindingsStack: CachedBindings[],
    traceState?: TraceState
  ): Promise<Either<QueryClientEvent, ClientEventQueryResult>> {
    let startTimestampValue: number | undefined = undefined;
    if (query.eventQuery.startTimestamp !== undefined && query.eventQuery.startTimestamp !== null) {
      const resolvedStartTimestamp = await this.evaluateFn(
        query.eventQuery.startTimestamp,
        queryResolver,
        bindingsStack,
        traceState
      );
      // startTimestamp could not be fully evaluated, return partially evaluated query as is:
      if (isLeft(resolvedStartTimestamp)) {
        return left({
          ...query,
          eventQuery: {
            ...query.eventQuery,
            startTimestamp: resolvedStartTimestamp.value as ConditionalExpression,
          },
        } as QueryClientEvent);
      }
      // startTimestamp could be evaluated (TODO: verify type is number)
      startTimestampValue = (await resolvedStartTimestamp).value as number;
    }
    const range = this.getRange(
      query.eventQuery.eventType,
      startTimestampValue,
      query.eventQuery.reverse,
      query.eventQuery.limit
    );
    let selectedEvents: Array<V2Event> = [];
    if (query.eventQuery.expression) {
      const evaluations: Promise<void>[] = [];
      const evalErrors: string[] = [];
      await range.each(wrappedEvent => {
        evaluations.push(
          (async () => {
            const result = await this.evaluateFilter(
              query.eventQuery.expression,
              wrappedEvent.timestamp,
              wrappedEvent.event,
              bindingsStack,
              traceState
            );
            if ('error' in result) {
              evalErrors.push(result.error);
            } else if ('value' in result && result.value === true) {
              selectedEvents.push(wrappedEvent.event);
            }
          })()
        );
      });
      await Promise.all(evaluations);
      if (evalErrors.length > 0) {
        const reportCount = Math.min(MAX_ERRORS_REPORTED, evalErrors.length);
        throw new JetfabricError(
          `ClientEvent expression could not be evaluated, ${
            evalErrors.length
          } errors encountered, first ${reportCount} are: ${evalErrors
            .slice(0, reportCount)
            .join(', ')}`
        );
      }
    } else {
      selectedEvents = (await range.toArray()).map(e => e.event);
    }
    if (query.eventQuery.aggregation) {
      // only 'count' supported for now
      if (query.eventQuery.aggregation === 'count') {
        return right(selectedEvents.length);
      } else {
        throw new JetfabricError(
          'ClientEventQueryV1 supplied invalid aggregation: ' +
            JSON.stringify(query.eventQuery.aggregation)
        );
      }
    }
    return right(selectedEvents);
  }
}
